import { useEffect, useState, createContext, useReducer } from 'react';
import * as type from './authType';
import _ from 'lodash';
import axios, { AxiosError } from 'axios';
import leedpulse from '../configs/leedpulse';
import { message } from 'antd-v4';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';
import { normalizeUser } from '@leedpulse-monorepo/utils';

// Firebase modular imports
import { auth, firestore } from '../configs/firebase';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  DocumentData,
  orderBy,
} from 'firebase/firestore';

// Reducer
import { reducer, initalState } from './authReducer';

// Services
import notificationService from '../services/NotificationService';
import subscriptionService from '../services/SubscriptionService';
import contactService from '../services/ContactService';
import jobService from '../services/JobService';
import statService from '../services/StatService';
import domainService from '../services/DomainService';
import userService, { Constraint } from '../services/UserService';
import { Contact, ISubscription, IUser } from '@leedpulse-monorepo/interfaces';
import { UserType } from '@leedpulse-monorepo/enums';

export interface FirebaseContextType {
  changePassword: () => Promise<void>;
  cleanUp_UI: () => void;
  _getUser: (uid: string, firstTry?: boolean) => Promise<void>;
  getUserByUid: (uid: string) => Promise<{ success: boolean; status: string; msg: string; doc: IUser } | { success: boolean; status: string; msg: string; doc: undefined }>;
  getUsersByConstraints: (constraints: Constraint[]) => Promise<{ success: boolean; status: string; msg: string; doc: IUser[] } | { success: boolean; status: string; msg: string; doc?: undefined }>;
  getAllSMSForSpecificContactId: (contactId: string) => Promise<{ success: boolean; status: string; msg: string; doc: any[] } | { success: boolean; status: string; msg: string; }>;
  getSubscriptionsByClaims: () => Promise<{ success: boolean; status: string; msg: string; doc: ISubscription[] } | { success: boolean; status: string; msg: string; }>;
  getSubscriptionById: (subscriptionId: string) => Promise<{ success: boolean; status: string; msg: string; doc: ISubscription } | { success: boolean; status: string; msg: string; doc: undefined }>;
  getAIById: (id: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData } | { success: boolean; status: string; msg: string; doc?: undefined } | { status: string; msg: any; success?: undefined; doc?: undefined; }>;
  getAIByApiKey: (apiKey: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData } | { success: boolean; status: string; msg: string; doc?: undefined }>;
  getDomainByAIId: (AIId: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData } | { success: boolean; status: string; msg: string; doc?: undefined } | { status: string; msg: any; success?: undefined; doc?: undefined; }>;
  getScenariosBySubscriptionId: (subscriptionId: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string; doc?: undefined }>;
  getScenariosByIds: (scenarioIds: string[]) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string; doc?: undefined }>;
  getJobsByContactId: (contactId: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string}>;
  getJobsByPeriod: (sTimestampBegin: number, sTimestampEnd: number, apiKey: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string}>;
  getTasksToDoByContactId: (sContactId: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string, doc?: undefined }>;
  getTasksDoneByContactId: (sContactId: string) => Promise<{ success: boolean; status: string; msg: string; doc: DocumentData[] } | { success: boolean; status: string; msg: string, doc?: undefined }>;
  getHomeStats: (apiKey: string, collaboratorUid: string, scenarioId: string, period: [Date, Date]) => Promise<{ success: boolean; status: string; msg: string; doc: any[] } | { success: boolean; status: string; msg: string}>;
  getCollaborators: () => Promise<{ status: string; msg: string; doc: { uid: string; firstname: string; lastname: string; email: string; }[] } | { sstatus: string; msg: string}>;
  getUsersBySubscriptionId: (subscriptionId: string, removeInternalUsers?: boolean) => Promise<{ success: boolean; msg: string; users: IUser[] } | { success: boolean; msg: string, users?: undefined }>;
  getSenderChannels: (senders: string[]) => Promise<Record<string, string | null>>
  user: User | null;
  userData: DocumentData | null;
  subscriptionId: string | null;
  signInUser: (data: { email: string; password: string }) => Promise<{ status: string; msg: any; }>;
  signOutUser: () => Promise<void>;
  _setSubscription: (subscriptionId: string) => Promise<void>;
  putAI: (oAI: { id: string; [key: string]: any; }) => Promise<{ success: boolean; }>;
  refreshUserData: (data: DocumentData) => void;
  putContact: (id: string, data: any, apiKey: string) => Promise<{ status: string; msg: string; }>
  countAllAndEngagedContacts: (apiKey: string) => Promise<{ success: boolean; status: string; msg: string; doc: number; engagedContactsCount: number; } | { success: boolean; status: string; msg: string; doc?: undefined; engagedContactsCount?: undefined; }>
  state: { loading: boolean; errorMsg: any; };
  isAutoConnecting: boolean;
  autoConnectError: string | null;
  isAuthInitialized: boolean;
}

export const FirebaseContext = createContext<FirebaseContextType>({
  changePassword: async () => { return Promise.resolve(); },
  cleanUp_UI: async () => { return Promise.resolve(); },
  _getUser: async () => { return Promise.resolve(); },
  getUserByUid: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Utilisateur non trouvé', doc: undefined }); },
  getUsersByConstraints: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Utilisateurs non trouvés' }); },
  getAllSMSForSpecificContactId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'SMS non trouvés' }); },
  getSubscriptionsByClaims: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Abonnements non trouvés' }); },
  getSubscriptionById: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Abonnement non trouvé', doc: undefined }); },
  getAIById: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'IA non trouvée', doc: undefined }); },
  getAIByApiKey: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'IA non trouvée', doc: undefined }); },
  getDomainByAIId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Domaine non trouvé', doc: undefined }); },
  getScenariosBySubscriptionId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Scénarios non trouvés', doc: [] }); },
  getScenariosByIds: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Scénarios non trouvés', doc: [] }); },
  getJobsByContactId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Jobs non trouvés', doc: [] }); },
  getJobsByPeriod: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Jobs non trouvés', doc: [] }); },
  getTasksToDoByContactId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Tâches non trouvées', doc: [] }); },
  getTasksDoneByContactId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Tâches non trouvées', doc: [] }); },
  getHomeStats: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Stats non trouvées', doc: [] }); },
  getCollaborators: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Collaborateurs non trouvés', doc: [] }); },
  getUsersBySubscriptionId: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Utilisateurs non trouvés', users: [] }); },
  getSenderChannels: async () => { return Promise.resolve({} as Record<string, string | null>); },
  user: null,
  userData: null,
  subscriptionId: null,
  signInUser: async () => { return Promise.resolve({ status: 'error', msg: 'Utilisateur non connecté' }); },
  signOutUser: async () => { return Promise.resolve(); },
  _setSubscription: async () => { return Promise.resolve(); },
  putAI: async () => { return Promise.resolve({ success: false }); },
  refreshUserData: (_: DocumentData) => { return Promise.resolve(); },
  putContact: async () => { return Promise.resolve({ status: 'error', msg: 'Contact non créé' }); },
  countAllAndEngagedContacts: async () => { return Promise.resolve({ success: false, status: 'error', msg: 'Contacts non trouvés', doc: 0, engagedContactsCount: 0 }); },
  state: { loading: false, errorMsg: null },
  isAutoConnecting: false,
  autoConnectError: null,
  isAuthInitialized: false,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<DocumentData | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [state, dispatch] = useReducer(reducer, initalState);
  const [isAutoConnecting, setIsAutoConnecting] = useState(false);
  const [autoConnectError, setAutoConnectError] = useState<string | null>(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  const DEBUG = false;

  useEffect(() => {
    // Check if there is an authentication code in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('auth');

    if (authCode) {
      // If an authentication code is present, attempt auto-connect
      autoConnect(authCode);
    }

    // Standard Firebase authentication listener configuration
    const unsubscribe = onAuthStateChanged(auth, (oUser) => {
      // Keep the router blocked until the first auth check is resolved.
      const resolveAuthState = async () => {
        try {
          if (oUser !== null) {
            setUser(oUser);
            await _getUser(oUser.uid);
          } else {
            setUser(null);
            setUserData(null);
            setSubscriptionId(null);
          }
        } finally {
          setIsAuthInitialized(true);
        }
      };

      resolveAuthState();
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to auto-connect with an authorization code
  const autoConnect = async (authCode: string) => {
    try {
      setIsAutoConnecting(true);
      setAutoConnectError(null);

      // Check the validity of the authorization code
      const verifyResponse = await axios.post(
        `${leedpulse.route_url}/api/v1/auth/verify`,
        {
          authorization_code: authCode,
        }
      );

      if (verifyResponse.data.success && verifyResponse.data.data.valid) {
        // Sign in with the custom token
        await signInWithCustomToken(auth, authCode);

        // Clean the URL by removing the auth parameter
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        message.success('Connecté avec succès', 3);

        dispatch({ type: type.AUTH_SUCCESS });
      } else {
        const errorMsg = "Code d'autorisation invalide";
        message.error(`Erreur d'auto-connexion: ${errorMsg}`, 4);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Auto-connect error:', error);

      // Handle the error
      let errorMsg;
      if (error instanceof AxiosError && error.response?.status === 401) {
        errorMsg = 'Token non valide ou expiré';
      } else if (error instanceof Error && error.message.includes('401')) {
        errorMsg = 'Token non valide ou expiré';
      } else {
        errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      }

      setAutoConnectError(errorMsg);
      message.error(`Erreur d'auto-connexion: ${errorMsg}`, 4);
      dispatch({ type: type.AUTH_ERROR, payload: errorMsg });
    } finally {
      setIsAutoConnecting(false);
    }
  };

  const signInUser = async (data: { email: string; password: string }) => {
    try {
      dispatch({ type: type.AUTH_TRY });
      const userCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      _getUser(userCredential.user.uid);
      dispatch({ type: type.AUTH_SUCCESS });
      return {
        status: 'success',
        msg: 'Bienvenue ' + userCredential.user.email,
      };
    } catch (error: any) {
      dispatch({ type: type.AUTH_ERROR, payload: error.message });
      return {
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const changePassword = async () => {
    try {
      if (!user?.email) {
        throw new Error('Utilisateur non connecté');
      }

      return await sendPasswordResetEmail(auth, user.email);
    } catch (error: any) {
      const msg = error.message;
      dispatch({ type: type.AUTH_ERROR, payload: msg });
    }
  };

  const getUserByUid = async (uid: string) => {
    try {
      const usersRef = collection(firestore, 'Users');
      const q = query(usersRef, where('uid', '==', uid));
      const querySnapshot = await getDocs(q);

      if (!subscriptionId) {
        throw new Error('Abonnement non trouvé');
      }

      if (!querySnapshot.empty) {
        const oUser = normalizeUser(querySnapshot.docs[0].data() as IUser, subscriptionId);
        return {
          success: true,
          status: 'success',
          msg: 'Utilisateur récupéré avec succès.',
          doc: oUser,
        };
      } else {
        return {
          success: false,
          status: 'error',
          msg: 'Utilisateur non trouvé',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const getUsersByConstraints = async (constraints: Constraint[] = []) => {
    if (!subscriptionId) {
      throw new Error('Abonnement non trouvé');
    }
    return await userService.getUsersByConstraints(constraints, subscriptionId);
  };

  const _getUser = async (uid: string, firstTry = true) => {
    try {
      const usersRef = collection(firestore, FirestoreCollections.users);
      const q = query(usersRef, where('uid', '==', uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.docs?.length > 0) {
        const oUserData = querySnapshot.docs[0].data();
        // id_subscription : Record<string, { roles: string[] }>
        const aSubscritionsIds = Object.keys(oUserData.id_subscription);
        // @deprecated role supervisor removed
        // .filter(idSubscription => 
        //   !oUserData.id_subscription[idSubscription]?.roles
        //     .some((role : UserType) => role === UserType.SUPERVISOR)
        // );

        if (aSubscritionsIds.length === 0) {
          throw new Error('Aucun abonnement trouvé pour cet utilisateur');
        }

        // Retrieve the subscription from the local storage
        let applyDefaultSubscription = false;

        // Get local storage values
        const localStorageSubscriptionId = localStorage.getItem(
          'leedpulse-subscriptionId'
        );
        const localStorageAI = localStorage.getItem('leedpulse-ai');

        let sLocalSubscriptionId = null;
        if (localStorageSubscriptionId) {
          // Check if the subscription is still valid
          if (!aSubscritionsIds.includes(localStorageSubscriptionId)) {
            flushLocalStorage();
            applyDefaultSubscription = true;
          } else {
            sLocalSubscriptionId = localStorageSubscriptionId;
            setSubscriptionId(localStorageSubscriptionId);

            // Update the AI in the local storage if needed
            if (!localStorageAI) {
              // Get the subscription doc
              const oSubscription = await getSubscriptionById(
                localStorageSubscriptionId
              );
              if (oSubscription.success && oSubscription.doc) {
                // Get the AI doc
                const oAI = await getAIById(oSubscription.doc.id_ai);
                if (oAI.success) {
                  localStorage.setItem('leedpulse-ai', JSON.stringify(oAI.doc));
                }
              }
            }
          }
        } else {
          applyDefaultSubscription = true;
        }

        // Apply the first subscription by default
        if (applyDefaultSubscription) {
          sLocalSubscriptionId = aSubscritionsIds[0];
          setSubscriptionId(aSubscritionsIds[0]);
          localStorage.setItem(
            'leedpulse-subscriptionId',
            aSubscritionsIds[0]
          );
        }

        if (!sLocalSubscriptionId) {
          throw new Error('Abonnement non trouvé');
        }

        // Put
        setUserData(normalizeUser(oUserData as IUser, sLocalSubscriptionId));
      }
    } catch (error: any) {
      // If this is the first attempt, try one more time
      if (firstTry) {
        await _getUser(uid, false);
      } else {
        // If this is not the first attempt, trigger a logout
        dispatch({
          type: type.AUTH_ERROR,
          payload:
            error.message ||
            'Erreur lors du chargement des données utilisateur',
        });

        // Trigger the logout
        await signOutUser();
      }
    }
  };

  const _setSubscription = async (subscriptionId: string) => {
    setSubscriptionId(subscriptionId);
  };

  const putAI = async (oAI: { id: string;[key: string]: any }) => {
    try {
      const docRef = doc(firestore, 'AI', oAI.id);
      const docSnap = await getDoc(docRef);
      // Check if docRef has been retrieved
      if (!docSnap.exists()) {
        return { success: false };
      }
      await updateDoc(docRef, oAI);
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const getAIByApiKey = async (apiKey: string) => {
    try {
      const aiRef = collection(firestore, 'AI');
      const q = query(aiRef, where('id_api_key', '==', apiKey));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.docs?.length > 0) {
        return {
          success: true,
          status: 'success',
          msg: 'IA récupérée avec succès.',
          doc: querySnapshot.docs[0].data(),
        };
      } else {
        return {
          success: false,
          status: 'error',
          msg: 'Aucune IA trouvée',
          doc: {},
        };
      }
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const getAIById = async (id: string) => {
    try {
      const token = await user?.getIdTokenResult();
      if (!token) {
        throw new Error('Token non trouvé');
      }

      const keys = token.claims.api_key;

      const aiRef = collection(firestore, 'AI');
      const q = query(aiRef, where('id', '==', id), where('id_api_key', 'in', keys));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.docs?.length > 0) {
        return {
          success: true,
          status: 'success',
          msg: 'IA récupérée avec succès.',
          doc: querySnapshot.docs[0].data(),
        };
      } else {
        return {
          success: false,
          status: 'error',
          msg: 'Aucune IA trouvée',
        };
      }
    } catch (error: any) {
      return {
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };


  const getDomainByAIId = async (AIId: string) => {
    const aiRes = await getAIById(AIId)
    if (!aiRes.success) return aiRes;
    return await domainService.getDomainById(aiRes.doc?.domain || '');
  };


  const getSubscriptionsByClaims = async () => {
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }
    return await subscriptionService.getSubscriptionsByClaims(user);
  };

  const getSubscriptionById = async (subId: string) => {
    return await subscriptionService.getSubscriptionById(subId);
  };

  const refreshUserData = (data: DocumentData) => {
    if (!subscriptionId) {
      throw new Error('Abonnement non trouvé');
    }
    const mergedUserData = _.merge(userData, data);
    setUserData(normalizeUser(mergedUserData as IUser, subscriptionId));
  };

  const putContact = async (id: string, data: any, apiKey: string) => {
    return await contactService.updateContact(id, data, apiKey);
  };

  const flushLocalStorage = async () => {
    console.log(
      '🗑️ Suppression des données locales de votre session Leedpulse'
    );
    localStorage.removeItem('leedpulse-subscriptionId');
    localStorage.removeItem('leedpulse-subscription');
    localStorage.removeItem('leedpulse-ai');
  };

  const signOutUser = async () => {
    try {
      // Signout
      await signOut(auth);

      // Remove all local storage items
      flushLocalStorage();

      // Redirect to home
      window.location.href = '/';
    } catch (error: any) {
      const msg = error.message;
      dispatch({ type: type.AUTH_ERROR, payload: msg });
    }
  };

  const cleanUp_UI = async () => dispatch({ type: type.AUTH_CLEAN_UP });

  // Forum

  const getAllSMSForSpecificContactId = async (contactId: string) => {
    if (DEBUG) {
      console.log('DEBUG CALL 🔥 - getAllSMSForSpecificContactId', contactId);
    }

    try {
      const token = await user?.getIdTokenResult();
      if (!token) {
        throw new Error('Token non trouvé');
      }

      const keys = token.claims.api_key;

      const smsRef = collection(firestore, 'SMS');
      const q = query(
        smsRef,
        where('id_contact', '==', contactId),
        where('id_api_key', 'in', keys)
      );
      const querySnapshot = await getDocs(q);

      const aSMS: { id_doc: string; }[] = [];
      querySnapshot?.docs.forEach((sms) => {
        const oSMS = sms.data();
        oSMS.id_doc = sms.id;
        aSMS.push(oSMS as { id_doc: string; });
      });

      return {
        success: true,
        status: 'success',
        msg: 'Liste des SMS récupérée avec succès.',
        doc: aSMS,
      };
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const getScenariosBySubscriptionId = async (subscriptionId: string) => {
    // Get subscription
    const oSubscription = await getSubscriptionById(subscriptionId);
    if (!oSubscription.doc || !oSubscription.doc?.api_key) {
      return {
        success: false,
        status: 'error',
        msg: oSubscription.msg,
      };
    }

    // Get scenarios
    return await getScenariosByIds(oSubscription.doc.id_scenarios);
  };

  const getScenariosByIds = async (scenarioIds: string[]) => {
    try {
      // Check if scenarioIds is empty or null
      if (!scenarioIds || scenarioIds.length === 0) {
        return {
          success: true,
          status: 'success',
          msg: 'Aucun scénario trouvé.',
          doc: [],
        };
      }

      const scenariosRef = collection(firestore, 'Scenarios');
      const q = query(scenariosRef, where('id', 'in', scenarioIds));
      const querySnapshot = await getDocs(q);

      const aScenarios: { id_doc: string; }[] = [];
      querySnapshot?.docs.forEach((scenario) => {
        const oScenario = scenario.data();
        aScenarios.push(oScenario as { id_doc: string; });
      });

      return {
        success: true,
        status: 'success',
        msg: 'Liste des scénarios récupérée avec succès.',
        doc: aScenarios,
      };
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const getJobsByContactId = async (contactId: string) => {
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }
    return await jobService.getJobsByContactId(user, contactId);
  };

  const getJobsByPeriod = async (sTimestampBegin: number, sTimestampEnd: number, apiKey: string) => {
    if (!userData) {
      throw new Error('Données utilisateur non trouvées');
    }

    return await jobService.getJobsByPeriod(
      userData,
      sTimestampBegin,
      sTimestampEnd,
      apiKey
    );
  };

  const getTasksToDoByContactId = async (sContactId: string) => {
    try {
      const tasksRef = collection(firestore, 'Tasks');
      const q = query(
        tasksRef,
        where('id_contact', '==', sContactId),
        where('status', '==', 'todo')
      );
      const querySnapshot = await getDocs(q);

      const aTasks: { id_doc: string; }[] = [];
      querySnapshot?.docs.forEach((oTask) => {
        const oCurrentTask = oTask.data();
        oCurrentTask.id_doc = oTask.id;
        aTasks.push(oCurrentTask as { id_doc: string; });
      });

      return {
        success: true,
        status: 'success',
        msg: 'Liste des Tâches récupérée avec succès.',
        doc: aTasks,
      };
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const getTasksDoneByContactId = async (sContactId: string) => {
    try {
      const tasksRef = collection(firestore, 'Tasks');
      const q = query(
        tasksRef,
        where('id_contact', '==', sContactId),
        where('status', '==', 'completed')
      );
      const querySnapshot = await getDocs(q);

      const aTasks: { id_doc: string; }[] = [];
      querySnapshot?.docs.forEach((oTask) => {
        const oCurrentTask = oTask.data();
        oCurrentTask.id_doc = oTask.id;
        aTasks.push(oCurrentTask as { id_doc: string; });
      });

      return {
        success: true,
        status: 'success',
        msg: 'Liste des Tâches récupérée avec succès.',
        doc: aTasks,
      };
    } catch (error: any) {
      return {
        success: false,
        status: 'error',
        msg: error.message.toLocaleString(),
      };
    }
  };

  const countAllAndEngagedContacts = async (apiKey: string) => {
    if (!userData) {
      throw new Error('Données utilisateur non trouvées');
    }

    return await contactService.countAllAndEngagedContacts(userData as IUser, apiKey);
  };

  const getHomeStats = async (apiKey: string, collaboratorUid: string, scenarioId: string, period: [Date, Date]) => {
    if (!userData) {
      throw new Error('Données utilisateur non trouvées');
    }
    return await statService.getHomeStats(
      userData as IUser,
      apiKey,
      collaboratorUid,
      scenarioId,
      period
    );
  };

  const getCollaborators = async () => {
    try {
      if (!subscriptionId) {
        throw new Error('Abonnement non trouvé');
      }

      const usersRef = collection(firestore, FirestoreCollections.users);
      const q = query(usersRef, orderBy(`id_subscription.${subscriptionId}`, 'asc'));
      const collaboratorsSnapshot = await getDocs(q);

      const collaborators = collaboratorsSnapshot.docs.map((doc) => {
        const data = normalizeUser(doc.data() as IUser, subscriptionId);
        return {
          uid: data.uid,
          firstname: data.firstname,
          lastname: data.lastname,
          email: data.email,
        };
      });

      return {
        status: 'success',
        msg: 'Collaborateurs récupérés avec succès.',
        doc: collaborators,
      };
    } catch (error: any) {
      console.error(
        'Erreur lors de la récupération des collaborateurs :',
        error
      );
      return {
        status: 'error',
        msg: error.message,
        doc: [],
      };
    }
  };

  const getUsersBySubscriptionId = async (
    subscriptionId: string,
    removeInternalUsers = false
  ) => {
    try {
      const usersRef = collection(firestore, FirestoreCollections.users);
      const q = query(usersRef, orderBy(`id_subscription.${subscriptionId}`, 'asc'));
      const usersSnapshot = await getDocs(q);

      if (usersSnapshot.empty) {
        console.warn('Aucun utilisateur trouvé pour cette souscription.');
        return {
          success: false,
          msg: 'Aucun utilisateur trouvé.',
          users: [] as IUser[],
        };
      }

      let users = usersSnapshot.docs.map((doc) => normalizeUser({
        id: doc.id,
        ...doc.data(),
      } as IUser, subscriptionId));

      // Remove internal users
      if (removeInternalUsers) {
        users = users.filter(
          (user) =>
            !(
              user.email &&
              (user.email.endsWith('@leedpulse.com') ||
                user.email === 'joeybronner@gmail.com')
            )
        );
      }

      return {
        success: true,
        msg: 'Utilisateurs récupérés avec succès.',
        users,
      };
    } catch (error: any) {
      console.error('Erreur lors de la récupération des utilisateurs :', error);
      return {
        success: false,
        msg: error.message,
        users: [] as IUser[],
      };
    }
  };

  const getSenderChannels = async (senders: string[]) => {
    try {
      const senderChannels: Record<string, string | null> = {};

      const senderPromises = senders.map(async (sender) => {
        const senderDocRef = doc(firestore, 'Numbers', sender);
        const senderDoc = await getDoc(senderDocRef);
        if (senderDoc.exists()) {
          senderChannels[sender] = senderDoc.data().channel;
        } else {
          senderChannels[sender] = null;
        }
      });

      await Promise.all(senderPromises);
      return senderChannels;
    } catch (error) {
      console.error('Erreur lors de la récupération :', error);
      return {};
    }
  };

  return (
    <FirebaseContext.Provider
      value={{
        changePassword,
        cleanUp_UI,
        _getUser,
        getUserByUid,
        getUsersByConstraints,
        getAllSMSForSpecificContactId,
        getSubscriptionsByClaims,
        getSubscriptionById,
        getAIById,
        getAIByApiKey,
        getDomainByAIId,
        getScenariosBySubscriptionId,
        getScenariosByIds,
        getJobsByContactId,
        getJobsByPeriod,
        getTasksToDoByContactId,
        getTasksDoneByContactId,
        getHomeStats,
        getCollaborators,
        getUsersBySubscriptionId,
        getSenderChannels,
        user,
        userData,
        subscriptionId,
        signInUser,
        signOutUser,
        _setSubscription,
        putAI,
        refreshUserData,
        putContact,
        countAllAndEngagedContacts,
        state,
        isAutoConnecting,
        autoConnectError,
        isAuthInitialized,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};